import cn from 'classnames';
import { Field } from 'formik';
import { useContext, useRef } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import { CustomDropdown } from 'components/common/forms/SelectField';
import Text from 'components/common/Text';
import { BrandContext, TEMPO } from 'context/BrandContext';
import useBrand from 'hooks/useBrand';

const FormField = ({
  containerClass = '',
  disabled = false,
  error = '',
  inputClass = '',
  inputMode = '',
  labelClass = '',
  onKeyUp = () => {},
  onBlur = () => {},
  optionButtonClass = '',
  optionsContainerClass = '',
  placeholder = '',
  type = 'text',
  children,
  label,
  labelBrandStylesOverrides = '',
  selectContainerClass = '',
  selectedFieldButtonClass = '',
  transitionGroupComponent = 'div',
  autoComplete,
  component,
  helperText,
  innerRef,
  maxLength,
  name,
  onChange,
  options,
  successMessage,
  required = false,
  variant,
}) => {
  const { brand } = useContext(BrandContext);
  const isTempo = useBrand(TEMPO);
  const isRefreshed = variant === 'refreshed';
  const helperRef = useRef();
  const alertOrSuccessRef = useRef();
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;

  const commonFieldProps = {
    id: name,
    name,
    onBlur,
    disabled,
    'aria-invalid': Boolean(error),
    'aria-required': required || undefined,
    'aria-describedby':
      [error ? errorId : '', helperText ? helperId : ''].filter(Boolean).join(' ') || undefined,
  };

  return (
    <div className={cn('relative', brand, containerClass)}>
      {label && (
        <label
          htmlFor={name}
          className={cn('mb-2 flex items-center gap-1', labelClass, brand, {
            h6: !labelBrandStylesOverrides,
            [labelBrandStylesOverrides]: labelBrandStylesOverrides,
          })}
        >
          {label}
          {required && (
            <>
              <span className="text-red-500" aria-hidden="true">
                *
              </span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </label>
      )}

      {component === 'select' ? (
        <Field
          {...commonFieldProps}
          component={CustomDropdown}
          error={error}
          options={options}
          {...(onChange ? { changeHandler: onChange } : {})}
          optionButtonClass={optionButtonClass}
          optionsContainerClass={optionsContainerClass}
          selectContainerClass={selectContainerClass}
          selectedFieldButtonClass={selectedFieldButtonClass}
          variant={variant}
        />
      ) : (
        <Field
          {...commonFieldProps}
          autoComplete={autoComplete}
          component={component}
          className={cn(brand, inputClass, {
            'text textInput px-3 leading-8': !isRefreshed,
            'text textInput h-[52px] rounded-2xl border border-cream-120 px-6 py-4 leading-5 focus:border-green-100':
              isRefreshed && !isTempo,
            'text textInput h-[52px] rounded-2xl border border-charcoal-60 px-6 py-4 leading-5 focus:border-tempo-blue-90':
              isRefreshed && isTempo,
            'border-red-100': Boolean(error) && !isTempo,
            'border-red-120': Boolean(error) && isTempo,
          })}
          inputMode={inputMode}
          innerRef={innerRef}
          maxLength={maxLength}
          onKeyUp={onKeyUp}
          placeholder={placeholder}
          type={type}
          {...(onChange ? { onChange } : {})}
        >
          {children}
        </Field>
      )}
      {Boolean(helperText) && (
        <CSSTransition timeout={200} classNames="fadeInOut" nodeRef={helperRef}>
          <div ref={helperRef} id={helperId}>
            <Text
              brandStylesOverrides="textSm text-light"
              className={cn({ 'pt-2 text-charcoal-90': isRefreshed })}
            >
              {helperText}
            </Text>
          </div>
        </CSSTransition>
      )}
      <TransitionGroup
        role={error ? 'alert' : 'status'}
        className={cn('crossfadeContainer mt-px', {
          'min-h-6': Boolean(error),
        })}
        component={transitionGroupComponent}
      >
        {(error || successMessage) && (
          <CSSTransition nodeRef={alertOrSuccessRef} timeout={200} classNames="fadeInOut">
            <div ref={alertOrSuccessRef} id={error ? errorId : undefined}>
              <Text
                className="crossfadeItem"
                brandStylesOverrides={cn('textSm', {
                  'text-negative': error,
                  'text-primary': successMessage,
                })}
              >
                {error || successMessage}
              </Text>
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    </div>
  );
};

FormField.propTypes = {
  disabled: PropTypes.bool,
  children: PropTypes.node,
  component: PropTypes.string,
  error: PropTypes.string,
  label: PropTypes.string.isRequired,
  labelClass: PropTypes.string,
  inputClass: PropTypes.string,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  labelBrandStylesOverrides: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
        group: PropTypes.string,
      }),
    ])
  ),
  placeholder: PropTypes.string,
  type: PropTypes.string,
  onKeyUp: PropTypes.func,
  onChange: PropTypes.func,
  maxLength: PropTypes.number,
  inputMode: PropTypes.string,
  autoComplete: PropTypes.string,
  transitionGroupComponent: PropTypes.string,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  containerClass: PropTypes.string,
  selectedFieldButtonClass: PropTypes.string,
  optionButtonClass: PropTypes.string,
  optionsContainerClass: PropTypes.string,
  onBlur: PropTypes.func,
  selectContainerClass: PropTypes.string,
  successMessage: PropTypes.string,
  required: PropTypes.bool,
  variant: PropTypes.oneOf(['refreshed']),
};

export default FormField;
